import AddStationModal from '../AddStationModal/AddStationModal'
import { StationApi } from 'api/StationApi'
import clsx from 'clsx'
import ActionsMenu, { ActionsMenuItem } from 'components/ActionsMenu/ActionsMenu'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import FavoriteStar from 'components/FavoriteStar/FavoriteStar'
import { ReactComponent as RemoveSvg } from 'images/icons/delete.svg'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { PATH } from 'utils/constants'
import getTestId from 'utils/getTestId'
import { ILot, IStation } from 'utils/interfaces'
import { lotTypeMap } from 'utils/lotInterfaces'
import {
  formatInventoryMismatchTitle,
  getStationFeedbackStatus,
  STATION_INFO_MISMATCH_BADGE,
  stationFeedbackLabel,
  stationFeedbackTitle,
  stationHasDiagnosticsError,
} from 'utils/stationFeedback'
import { formatStationErrorMsg } from 'utils/stationErrorMsg'
import {
  getStationOccupancyStatusText,
  LotLiveTelemetry,
} from 'pages/Assets/stations/stationLotOccupancy'

import style from './StationItem.module.css'

const StationItem = ({
  station,
  onStationUpdated,
  lots,
  stations,
  liveLotTelemetryByLotId,
  hightlighted,
  onStationsChanged,
  hideDelete,
  disableEdit,
}: {
  station: IStation
  lots: ILot[]
  stations: IStation[]
  liveLotTelemetryByLotId?: Record<number, LotLiveTelemetry>
  onStationUpdated: () => void
  hightlighted: boolean
  onStationsChanged: () => void
  hideDelete?: boolean
  disableEdit?: boolean
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const goToStation = (station_id: number) => {
    navigate(`${PATH.STATION}${station_id}`)
  }
  const [deleteStationDialog, setDeleteStationDialog] = useState(false)
  const [editingStation, setEditingStation] = useState(false)

  const deleteStation = async () => {
    try {
      await StationApi.deleteStation(station.id)
      setDeleteStationDialog(false)
      onStationUpdated()
    } catch (error) {
      console.log('Error deleting station:')
    }
  }

  const stationLots = lots.filter(lot => lot.robot_station === station.id)
  const feedback = getStationFeedbackStatus(station)
  const hasDiagError = stationHasDiagnosticsError(station)
  // Offline / no_feedback: never surface stale occupied SN from history or Redis.
  const occupancyStatus =
    feedback === 'online'
      ? getStationOccupancyStatusText(stationLots, liveLotTelemetryByLotId)
      : null
  const showInventoryBadge = Boolean(station.inventory_mismatch && feedback === 'online')
  const showNoLiveStatusBadge = feedback === 'no_feedback'
  const rowTitle =
    formatInventoryMismatchTitle(station) ||
    (hasDiagError
      ? formatStationErrorMsg(station.diagnostics_error_msg) || undefined
      : undefined) ||
    stationFeedbackTitle(feedback) ||
    undefined

  return (
    <>
      <div
        className={clsx(style.tableRow, {
          [style.highlighted]: hightlighted,
          [style.errorRow]: hasDiagError,
          [style.offlineRow]: feedback === 'offline',
          [style.noFeedbackRow]: feedback === 'no_feedback',
        })}
        title={rowTitle}
        onClick={() => goToStation(station.id)}
        key={station.id}
      >
        <FavoriteStar kind="station" id={station.id} />
        <span className={clsx(style.tableCol, style.serialCol)}>{station.station_id}</span>
        <span className={clsx(style.tableCol, style.nameCol)}>{station.name}</span>
        <span className={clsx(style.tableCol, style.lotsCol)}>
          {stationLots.map(lot => lotTypeMap[lot.type]).join(', ')}
        </span>
        <span className={clsx(style.tableCol, style.robotsCol)}>
          {occupancyStatus
            ? occupancyStatus
            : !showNoLiveStatusBadge && !showInventoryBadge
              ? '—'
              : null}
          {showNoLiveStatusBadge && (
            <span className={style.noFeedbackBadge} title={stationFeedbackTitle(feedback)}>
              {stationFeedbackLabel(feedback)}
            </span>
          )}
          {showInventoryBadge && (
            <span className={style.mismatchBadge} title={formatInventoryMismatchTitle(station)}>
              {STATION_INFO_MISMATCH_BADGE}
            </span>
          )}
        </span>
        <div className={style.colCenter}>
          <ActionsMenu>
            <ActionsMenuItem
              onClick={() => setEditingStation(true)}
              disabled={disableEdit}
              data-test-id={getTestId('edit-zone-list')}
            >
              <span className={style.menuItemIcon} />
              <span className={style.menuItemText} data-test-id={getTestId('edit-title')}>
                {t('common.edit')}
              </span>
            </ActionsMenuItem>
            {!hideDelete && (
              <ActionsMenuItem
                onClick={() => setDeleteStationDialog(true)}
                data-test-id={getTestId('remove-zone-list')}
                disabled={Boolean(station.cannot_delete_reason)}
                title={station.cannot_delete_reason || undefined}
              >
                <RemoveSvg className={style.menuItemIcon} />
                <span data-test-id={getTestId('delete-title')}>{t('common.delete')}</span>
              </ActionsMenuItem>
            )}
          </ActionsMenu>
        </div>
      </div>
      <ConfirmDialog
        title={t('stations.delete_question')}
        isOpen={deleteStationDialog}
        onClose={() => setDeleteStationDialog(false)}
        onConfirmClick={deleteStation}
        confirmText={t('common.delete')}
        danger={true}
      />
      <AddStationModal
        isOpen={editingStation}
        stations={stations}
        onClose={() => setEditingStation(false)}
        onSave={onStationUpdated}
        stationExisting={station}
        stationLots={stationLots}
        onStationsChanged={onStationsChanged}
      />
    </>
  )
}

export default StationItem
